import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  es: {
    title: 'Solicitar Información',
    description: 'Completa el formulario y nos pondremos en contacto contigo lo antes posible.',
    firstName: 'Nombre',
    lastName: 'Apellido',
    email: 'Email',
    phone: 'Teléfono',
    comments: 'Comentarios (opcional)',
    commentsPlaceholder: 'Cuéntanos más sobre tu interés en la propiedad...',
    submit: 'Enviar Solicitud',
    required: 'Obligatorio',
    emailOrPhone: 'Email o teléfono requerido',
    successTitle: '¡Solicitud Enviada!',
    successMessage: 'Gracias por tu interés. Nos pondremos en contacto contigo pronto.',
    close: 'Cerrar',
    errorMessage: 'Ha ocurrido un error. Por favor, inténtalo de nuevo.',
    errors: {
      firstName: 'El nombre es obligatorio',
      lastName: 'El apellido es obligatorio',
      contact: 'Debes proporcionar un email o un número de teléfono'
    }
  },
  en: {
    title: 'Request Information',
    description: 'Fill out the form and we will contact you as soon as possible.',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    comments: 'Comments (optional)',
    commentsPlaceholder: 'Tell us more about your interest in the property...',
    submit: 'Send Request',
    required: 'Required',
    emailOrPhone: 'Email or phone required',
    successTitle: 'Request Sent!',
    successMessage: 'Thank you for your interest. We will contact you soon.',
    close: 'Close',
    errorMessage: 'An error occurred. Please try again.',
    errors: {
      firstName: 'First name is required',
      lastName: 'Last name is required',
      contact: 'You must provide an email or phone number'
    }
  }
};

const ContactFormModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const t = translations[language];
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    comments: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t.errors.firstName;
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = t.errors.lastName;
    }
    
    if (!formData.email.trim() && !formData.phone.trim()) {
      newErrors.contact = t.errors.contact;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(false);
    
    try {
      // Send to backend API
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear related errors when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if ((field === 'email' || field === 'phone') && errors.contact) {
      setErrors(prev => ({ ...prev, contact: undefined }));
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      comments: ''
    });
    setErrors({});
    setIsSubmitted(false);
    setSubmitError(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md w-full bg-white p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-stone-800 text-white p-6">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-white">
              {isSubmitted ? t.successTitle : t.title}
            </DialogTitle>
            <DialogDescription className="text-white/80 mt-2">
              {isSubmitted ? t.successMessage : t.description}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
              {t.errorMessage}
            </div>
          )}
          {isSubmitted ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <Button
                onClick={handleClose}
                className="mt-4 bg-stone-800 hover:bg-stone-700 text-white"
              >
                {t.close}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* First Name */}
              <div>
                <Label htmlFor="firstName" className="text-stone-700 text-sm">
                  {t.firstName} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  className={`mt-1 ${errors.firstName ? 'border-red-500' : ''}`}
                  placeholder={t.firstName}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <Label htmlFor="lastName" className="text-stone-700 text-sm">
                  {t.lastName} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  className={`mt-1 ${errors.lastName ? 'border-red-500' : ''}`}
                  placeholder={t.lastName}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-stone-700 text-sm">
                  {t.email} <span className="text-amber-600 text-xs">({t.emailOrPhone})</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  className={`mt-1 ${errors.contact ? 'border-red-500' : ''}`}
                  placeholder="ejemplo@email.com"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-stone-700 text-sm">
                  {t.phone} <span className="text-amber-600 text-xs">({t.emailOrPhone})</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  className={`mt-1 ${errors.contact ? 'border-red-500' : ''}`}
                  placeholder="+34 600 000 000"
                />
                {errors.contact && (
                  <p className="text-red-500 text-xs mt-1">{errors.contact}</p>
                )}
              </div>

              {/* Comments */}
              <div>
                <Label htmlFor="comments" className="text-stone-700 text-sm">
                  {t.comments}
                </Label>
                <Textarea
                  id="comments"
                  value={formData.comments}
                  onChange={handleChange('comments')}
                  className="mt-1 min-h-[80px] resize-none"
                  placeholder={t.commentsPlaceholder}
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-stone-800 hover:bg-stone-700 text-white py-6 text-sm tracking-wider uppercase"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {language === 'es' ? 'Enviando...' : 'Sending...'}
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Send size={16} className="mr-2" />
                    {t.submit}
                  </span>
                )}
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormModal;
